/**
 * Appearance (docs/ux/design-language.md): the app opens **dark** regardless of the OS. Settings ›
 * Appearance offers Dark (default) · Light · Match system. `data-theme` on <html> is always an
 * explicit `light` or `dark`; "Match system" resolves it from `prefers-color-scheme` and follows
 * OS changes while it is chosen.
 */
import type { Settings } from './types.js'

const KEY = 'viko.theme'
export type ThemeChoice = Settings['theme']
export const DEFAULT_THEME_CHOICE: ThemeChoice = 'dark'
/** Settings order and labels. */
export const THEME_CHOICES: { id: ThemeChoice; label: string }[] = [
  { id: 'dark', label: 'Dark (default)' },
  { id: 'light', label: 'Light' },
  { id: 'system', label: 'Match system' },
]

const systemQuery = () =>
  typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: light)') : null

/** The concrete theme a choice renders as right now. */
export function resolveTheme(theme: ThemeChoice): 'light' | 'dark' {
  if (theme === 'system') return systemQuery()?.matches ? 'light' : 'dark'
  return theme
}

let unfollow: (() => void) | undefined

export function applyTheme(theme: ThemeChoice) {
  const root = document.documentElement
  unfollow?.()
  unfollow = undefined
  root.setAttribute('data-theme', resolveTheme(theme))
  root.dataset.themeChoice = theme
  if (theme === 'system') {
    const q = systemQuery()
    const on = () => root.setAttribute('data-theme', resolveTheme('system'))
    q?.addEventListener?.('change', on)
    unfollow = () => q?.removeEventListener?.('change', on)
  }
  try {
    localStorage.setItem(KEY, theme)
  } catch {
    // ignore
  }
}

export function storedTheme(): ThemeChoice {
  try {
    const t = localStorage.getItem(KEY)
    return t === 'light' || t === 'dark' || t === 'system' ? t : DEFAULT_THEME_CHOICE
  } catch {
    return DEFAULT_THEME_CHOICE
  }
}

/** ⌘K "Cycle theme": dark → light → match system → dark. */
export function nextTheme(t: ThemeChoice): ThemeChoice {
  return t === 'dark' ? 'light' : t === 'light' ? 'system' : 'dark'
}

/** The theme currently rendered (for previews that follow the app, e.g. option boards). */
export function currentTheme(): 'light' | 'dark' {
  if (typeof document === 'undefined') return 'dark'
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark'
}
